import { computed, signal, Service } from '@angular/core';

export interface NumericKeypadTarget {
  press(key: string): void;
  dismiss(): void;
}

@Service()
export class NumericKeypadService {
  private readonly activeTarget = signal<NumericKeypadTarget | null>(null);
  private readonly suppressedSignal = signal(false);
  private readonly heightPxSignal = signal(0);

  readonly isOpen = computed(() => this.activeTarget() !== null);

  /** Hides the keypad without clearing the target because `position` fixed content inside the sliding screen stack would otherwise slide with it. */
  readonly suppressed = this.suppressedSignal.asReadonly();

  readonly heightPx = this.heightPxSignal.asReadonly();

  readonly reservedSpacePx = computed(() =>
    this.isOpen() && !this.suppressed() ? this.heightPx() : 0
  );

  setSuppressed(value: boolean): void {
    this.suppressedSignal.set(value);
  }

  /**
   * Only positive measurements are kept because two keypad instances co-exist across
   * a task-detail slide, and the departing one must not zero out the height
   * the surviving one reported. A suppressed sheet also measures 0.
   */
  setHeightPx(value: number): void {
    if (value > 0) {
      this.heightPxSignal.set(value);
    }
  }

  clearHeight(): void {
    this.heightPxSignal.set(0);
  }

  open(target: NumericKeypadTarget): void {
    this.activeTarget.set(target);
  }

  close(target?: NumericKeypadTarget): void {
    if (!target || this.activeTarget() === target) {
      this.activeTarget.set(null);
    }
  }

  dismiss(): void {
    this.activeTarget()?.dismiss();
  }

  pressKey(key: string): void {
    this.activeTarget()?.press(key);
  }
}
